import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  clearTokens,
  getToken,
  loginTelegram,
  request,
  type UserVO,
} from "../lib/api";
import { resolveInitData, useTelegramReady, haptic } from "../lib/telegram";

type AuthState = {
  user: UserVO | null;
  loading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => void;
  setUser: (u: UserVO | null) => void;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<UserVO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useTelegramReady();

  const login = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const init = resolveInitData();
      if (!init) {
        throw new Error(
          "未获取到 Telegram initData。请在 Telegram 内打开，或确认开发模式 VITE_DEV_BYPASS / ESIM_TMA_DEV_BYPASS",
        );
      }
      const data = await loginTelegram(init);
      if (!data?.user) {
        throw new Error("登录成功但未返回用户信息");
      }
      setUserState(data.user);
      haptic("success");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "登录失败";
      setError(msg);
      haptic("error");
      // 不 rethrow：避免按钮 onClick 未 catch 导致控制台 Uncaught + 体验异常
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setUserState(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      try {
        if (!getToken()) {
          await login();
          return;
        }
        // 有 token：/me；后端可能返回 {user} 或扁平 UserVO
        try {
          const me = await request<UserVO & { user?: UserVO }>("/api/v1/me");
          const u = (me as { user?: UserVO })?.user || (me as UserVO);
          if (!cancelled && u && (u.id || u.email)) {
            setUserState(u as UserVO);
            return;
          }
        } catch {
          /* fallthrough re-login */
        }
        if (!cancelled) await login();
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "初始化失败");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void boot();
    // 安全超时：避免永久转圈白屏感
    const t = window.setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 12000);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boot once
  }, []);

  const setUser = useCallback((u: UserVO | null) => {
    setUserState(u);
  }, []);

  const value = useMemo(
    () => ({ user, loading, error, login, logout, setUser }),
    [user, loading, error, login, logout, setUser],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside provider");
  return v;
}
